import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ScheduleDetails } from "src/app/common/interfaces/home/schedule-details.interface";
import { AppConstants } from "src/app/common/constants/app-constants";
import { Router } from "@angular/router";
import { LandingPageService } from "../services/landing-page.service";
import { StorageService } from "src/app/common/services/storage.service";
import { distinctUntilChanged, finalize, Subscription } from "rxjs";
import { LandingClientService } from "src/app/common/services/landing-page.client";
import {
  ButtonVariant,
  ButtonComponent,
} from "src/app/common/components/core/button/button.component";
import { ConfirmationModalComponent } from "src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { HttpErrorResponse } from "@angular/common/http";
import { environment } from "environment";

@Component({
  selector: "app-schedule",
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  templateUrl: "./schedule.component.html",
  styleUrls: ["./schedule.component.scss"],
})
export class ScheduleComponent implements OnInit {
  schedules!: ScheduleDetails;
  emDash = AppConstants.emDash;
  facilityId: string | null = "";
  private facilitySubscription: Subscription | null = null;
  scheduleReport!: any;
  buttonVariant = ButtonVariant;

  constructor(
    private router: Router,
    private landingScreenService: LandingPageService,
    private storageService: StorageService,
    private landingClientService: LandingClientService,
    private matDialog: MatDialog
  ) {}

  async ngOnInit() {
    this.facilitySubscription = this.storageService.isSelectedFacility
      .pipe(distinctUntilChanged())
      .subscribe((facilityId) => {
        this.facilityId = facilityId;
        this.getScheduleDetails();
      });
  }

  ngOnDestroy() {
    this.facilitySubscription?.unsubscribe();
  }

  async getScheduleDetails() {
    this.schedules =
      await this.landingScreenService.getScheduleAndBillingDetails();
  }

  routeToPatientProfile(patientId: string) {
    if (environment.enableNewReactApp) {
      window.location.href = `/rc/patient/${this.facilityId}/profile/${patientId}`;
      return;
    }
    this.router.navigateByUrl(
      `/patient/${this.facilityId}/profile/${patientId}`
    );
  }

  printScheduleReport(): void {
    if (!this.facilityId || this.landingClientService.isPrintInProgress) return;
    
    this.landingClientService.isPrintInProgress = true;

    this.landingClientService.getScheduledReport(this.facilityId)
    .pipe(
      finalize(() => {
        this.landingClientService.isPrintInProgress = false;
      })
    )
    .subscribe(
      (response) => {
        if (response.body != null) {
          const date = new Date().toISOString().split("T")[0];
          const user = JSON.parse(this.storageService.getUser()!);
          const therapistName = user.fullname;

          const filename = `TherapistAgenda_${date}_${therapistName}.xlsx`;

          const fileURL = URL.createObjectURL(response.body);

          const a = document.createElement("a");
          a.href = fileURL;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          URL.revokeObjectURL(fileURL);
        }
      },
      (error: HttpErrorResponse) => {
        // Case 1: Backend sent JSON error as Blob
          if (error.error instanceof Blob) {
            const reader = new FileReader();

            reader.onload = () => {
              try {
                const errorJson = JSON.parse(reader.result as string);

                // Business error → keep default message
                if (errorJson.responseData === AppConstants.NoDataReports) {
                  this.displayErrorModal(AppConstants.warning);
                } 
                // Case 2: Any other backend error → show backend message
                else if (errorJson.responseData) {
                  this.displayErrorModal(AppConstants.error, AppConstants.DefaultPrintError);
                }
                // Case 3: Unexpected structure
                else {
                  this.displayErrorModal(AppConstants.error, AppConstants.DefaultPrintError);
                }
              } catch {
                // Parsing failed
                this.displayErrorModal(AppConstants.error, AppConstants.DefaultPrintError);
              }
            };

            reader.readAsText(error.error);
          } 
          // Network / unexpected error
          else {
            this.displayErrorModal(AppConstants.error, AppConstants.DefaultPrintError);
          }
        }
    );
  }

  checkIsPrintInProgress(){
    return this.landingClientService.isPrintInProgress;
  }

  private displayErrorModal(title?: string, message?: string): void {
    this.matDialog.open(ConfirmationModalComponent, {
      data: {
        title: title ?? AppConstants.error,
        message: message ?? AppConstants.NoAgendaForTheDay,
        buttonText: AppConstants.ok,
        hideCancelButton: true,
        isError: true,
      },
    });
  }
}
